// PHASE_1 / B0 reference slice + B1 queue actions — the leave Content component
// (ADR-014 §2). Faz 8.6 / Batch A1 restyles the VISUAL layer to the novem
// request-center language (NovemHR-ekranlar requests.jsx + emp-requests.jsx
// port): serif PageHeader with a role-aware lead, the KPI StatCard strip, the
// segmented status-filter tabs with count pills, and the novem table language
// (Personel avatar cell, status Badge tones, locale-correct date ranges). The
// DATA layer is untouched — same useKeysetQuery fetch, same decision
// mutations, same capability gates.
//
// LeaveListContent is a PURE, shell-agnostic Content component (the *Content
// convention B0 establishes — see the phase doc DS notes). The SAME component
// renders under the SINGLE parameterized AppShell in BOTH contexts: /requests
// + /leave use the server's actor-current decision inbox, while /me/requests
// + /me/leave retain normal self-history. One implementation; the view input
// is structural (ADR-014 §2), never a client-side eligibility guess.
//
// B1 added the QUEUE ACTIONS column: approve / reject on the management inbox
// and cancel on self history. Each button opens the shared confirmation dialog
// (features/leave/use-leave-decisions — reject asks for its reason) instead of
// firing on click. The buttons are hidden by <ScopeGate> off the
// caller's RESOLVED effective permissions (never the navigation hat) —
// CONVENIENCE ONLY; the server re-checks every mutation (ADR-017; the frontend
// is never a trust boundary, ADR-008). A self caller who somehow fires approve
// gets a 403 from the route regardless.
//
// It renders the keyset page from GET /v1/leave/requests via DataTable +
// useKeysetQuery (BUILD_PLAN §5 keyset, never OFFSET). Hours are DECIMALS
// (`_hours`), never money — nothing here goes through <Money> (Phase 1
// money/hours boundary).
//
// The Personel cell shows the real name: SEM-301 added the employee_name join
// to GET /v1/leave/requests, and the short employee_id survives only as the
// null-name fallback (unresolved/redacted subject).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarClock,
  CircleCheck,
  CircleX,
  Clock3,
  FileText,
  Hourglass,
  Inbox,
  Plus,
  Users,
} from "lucide-react";
import { Link as RouterLink } from "react-router";

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  PageHeader,
  ScopeGate,
  StatCard,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  type DataTableColumn,
} from "@novemai/components";

import { platformGraph } from "@/api/platform-graph";
import {
  errorMessage,
  unwrapEnvelope,
  type EnvelopedFetchResult,
} from "@/api/errors";
import { useKeysetQuery, type KeysetPage } from "@/api/keyset";
import { downloadLeaveRecordPdf, type LeaveRequestRow } from "@/features/leave/leave-api";
import {
  LEAVE_CAP_APPROVE,
  LEAVE_CAP_FILE,
  LEAVE_CAP_REJECT,
  leaveCapabilities,
} from "@/features/leave/leave-capabilities";
import { LEAVE_STATUS_TONE, displayLeaveStatus } from "@/features/leave/leave-status";
import { useLeaveDecisions } from "@/features/leave/use-leave-decisions";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { formatDate, formatDateRange } from "@/lib/date-range";
import { useTabParam } from "@/lib/use-tab-param";
import { formatHours } from "@/lib/hours";
import { formatCountWithMore } from "@/lib/count";
import { PersonLink } from "@/components/person-link";

// openapi-fetch doesn't know this path yet (the route carries no schema: block,
// so it's absent from the generated `paths`); the GET call is cast to the
// keyset contract + the canonical enveloped shape. Tightens automatically when
// the route migrates to a schema: block + the types regenerate.
type LeaveGet = (
  path: "/v1/leave/requests",
  init: {
    params: {
      query: {
        cursor?: string;
        status?: string;
        awaiting_my_decision?: boolean;
        mine?: boolean;
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<LeaveRequestRow>>>;

// The status tabs filter SERVER-SIDE: the backend GET /v1/leave/requests accepts
// an optional `status` enum (draft|submitted|approved|rejected|cancelled|taken),
// so the active tab is pushed into the query rather than filtered against the
// loaded keyset window (which would silently hide rows beyond the page).
async function fetchLeavePage(
  cursor: string | null,
  status?: string,
  awaitingMyDecision = false,
  mine = false,
): Promise<KeysetPage<LeaveRequestRow>> {
  const get = platformGraph.GET as unknown as LeaveGet;
  const result = get("/v1/leave/requests", {
    params: {
      query: {
        ...(cursor !== null && { cursor }),
        ...(status !== undefined && { status }),
        ...(awaitingMyDecision && { awaiting_my_decision: true }),
        ...(mine && { mine: true }),
      },
    },
  });
  return unwrapEnvelope(result);
}

// Status filter tabs (mockup REQ_TABS): Tümü / Bekleyen / Onaylanan /
// Reddedilen. "all" passes everything (incl. draft/cancelled/taken); the
// other three filter on the canonical status value.
const STATUS_TABS = [
  { value: "all", labelKey: "leave.list.tab_all" },
  { value: "submitted", labelKey: "leave.list.tab_pending" },
  { value: "approved", labelKey: "leave.list.tab_approved" },
  { value: "rejected", labelKey: "leave.list.tab_rejected" },
] as const;

export type LeaveListView = "management" | "self";

// The record document is bilingual (en-US / tr-TR). The viewer's UI language picks the
// copy; any other language falls back to the server's own choice (the subject's stored
// preference, then tr-TR) instead of guessing here.
function recordLocale(language: string): "en-US" | "tr-TR" | undefined {
  if (language.startsWith("tr")) return "tr-TR";
  if (language.startsWith("en")) return "en-US";
  return undefined;
}

export interface LeaveListContentProps {
  /** Management routes show only server-authoritative current assignments. */
  readonly view?: LeaveListView;
  /**
   * Where "Yeni talep" hands the requester. The leave surfaces keep the leave
   * form; the request center passes its type chooser (SEM-6355) so a second
   * request type is reachable from the button at all.
   */
  readonly newRequestHref?: string;
}

export function LeaveListContent({
  view = "self",
  newRequestHref = "/me/leave/file",
}: LeaveListContentProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  // Permission-derived (default-closed while loading): a decision button that
  // appears a beat late beats offering an action that will 403.
  const { capabilities: allowedCells } = useEffectivePermissions();
  const capabilities = leaveCapabilities(allowedCells);
  const isManagementView = view === "management";
  const canDecide = isManagementView && capabilities.has(LEAVE_CAP_APPROVE);
  const [statusTab, setStatusTab] = useTabParam(
    "status",
    STATUS_TABS.map((tab) => tab.value),
    "all",
  );
  const activeStatusTab = isManagementView ? "submitted" : statusTab;

  // The active tab drives the SERVER-SIDE filter. Management views are the
  // decision inbox: their only legal status is submitted and the actor-current
  // query is the authority. Self views retain the ordinary status history.
  const statusFilter = isManagementView
    ? "submitted"
    : activeStatusTab === "all"
      ? undefined
      : activeStatusTab;
  const queryScope = isManagementView ? "awaiting-my-decision" : "self-history";
  const query = useKeysetQuery<LeaveRequestRow>({
    queryKey: ["leave", "requests", queryScope, activeStatusTab],
    fetchPage: (cursor) => fetchLeavePage(cursor, statusFilter, isManagementView, !isManagementView),
  });

  // The KPI strip + lead summarize the full self-history window, independent
  // of an active status tab. In management mode the same actor-current,
  // submitted-only query backs both the table and summary (React Query de-dupes
  // the matching key); a management KPI must never count non-current rows.
  const summary = useKeysetQuery<LeaveRequestRow>({
    queryKey: [
      "leave",
      "requests",
      queryScope,
      isManagementView ? "submitted" : "all",
    ],
    fetchPage: (cursor) =>
      fetchLeavePage(
        cursor,
        isManagementView ? "submitted" : undefined,
        isManagementView,
        !isManagementView,
      ),
  });

  // Approve / reject / cancel run through the shared confirmation dialog. The
  // route stays the authority: a denied decision (403 auth.scope_forbidden, 409
  // approval.conflict, infra) is shown inside that dialog, and a success
  // invalidates every status-filtered request list, so a single-step decision
  // (SEM-2637 W1) leaves the "submitted" tab on the next render.
  const decisions = useLeaveDecisions();

  const [recordFailed, setRecordFailed] = useState(false);
  const downloadRecord = async (row: LeaveRequestRow): Promise<void> => {
    setRecordFailed(false);
    try {
      await downloadLeaveRecordPdf(
        row.request_id,
        `leave-record-${row.request_id}.pdf`,
        recordLocale(i18n.language),
      );
    } catch {
      setRecordFailed(true);
    }
  };

  // Self status counts use unfiltered loaded history; management derives only
  // inbox-visible facts from its submitted-only window. "+" while hasNextPage
  // signals further pages — the dashboard golden-sample convention.
  const pendingCount = summary.items.filter((r) => r.status === "submitted").length;
  const approvedCount = summary.items.filter((r) => r.status === "approved").length;
  const rejectedCount = summary.items.filter((r) => r.status === "rejected").length;
  const totalCount = summary.items.length;
  const totalHours = summary.items.reduce((sum, row) => sum + Number(row.hours), 0);
  const requesterCount = new Set(summary.items.map((row) => row.employee_id)).size;
  const earliestStartDate = summary.items.reduce<string | null>(
    (earliest, row) =>
      earliest === null || row.start_date < earliest ? row.start_date : earliest,
    null,
  );
  // The true earliest submitted request may sit beyond the loaded window
  // while more pages remain — asserting a date here could be flatly wrong,
  // not just imprecise (unlike the hedged hours/requester counts, a date
  // cannot carry a "+"). Refuse to claim one rather than claim a wrong one.
  const earliestStartLabel =
    summary.hasNextPage || earliestStartDate === null
      ? t("common.unknown")
      : formatDate(i18n.language, earliestStartDate);

  const fmtRange = (startIso: string, endIso: string): string =>
    formatDateRange(i18n.language, startIso, endIso);

  const columns: readonly DataTableColumn<LeaveRequestRow>[] = [
    {
      id: "employee",
      header: t("leave.list.column_employee"),
      // SEM-301: the leave API now resolves the subject's display name. Show it
      // as the primary line (over the short employee_id), falling back to the
      // short id when the name is unresolved/redacted; the avatar silhouette
      // still stands in for the person's photo.
      cell: (row): React.ReactNode => (
        <div className="flex items-center gap-2.5">
          <Avatar size={32} alt={row.employee_name ?? row.employee_id.slice(0, 8)} />
          {/* The Avatar already carries the name as its accessible label, so
              only the text is wrapped — wrapping both would announce it twice. */}
          <PersonLink employeeId={row.employee_id}>
            <div className="whitespace-nowrap font-serif text-[14.5px] text-[var(--fg-primary)]">
              {row.employee_name ?? row.employee_id.slice(0, 8)}
            </div>
          </PersonLink>
        </div>
      ),
    },
    {
      id: "leave_type",
      header: t("leave.column.leave_type"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[13px] text-[var(--fg-secondary)]">
          {row.policy_name ?? t("common.unknown")}
        </span>
      ),
    },
    {
      id: "dates",
      header: t("leave.column.dates"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12.5px] text-[var(--fg-secondary)]">
          {fmtRange(row.start_date, row.end_date)}
        </span>
      ),
    },
    {
      id: "hours",
      header: t("leave.column.hours"),
      // Decimal hours, formatted as a plain localized number — NOT money.
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[13px] font-medium text-[var(--fg-secondary)]">
          {t("leave.list.hours_value", {
            hours: formatHours(row.hours, i18n.language) ?? t("common.unknown"),
          })}
        </span>
      ),
    },
    {
      id: "status",
      header: t("leave.column.status"),
      cell: (row): React.ReactNode => {
        const shown = displayLeaveStatus(row);
        return (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={LEAVE_STATUS_TONE[shown] ?? "neutral"}>
              {t(`leave.status.${shown}`)}
            </Badge>
            {/* Why it ended this way lives with the outcome, apart from the
                requester's own "Açıklama": the two used to share a column, so a
                rejection reason read as if the employee had written it. */}
            {row.decision_reason != null &&
              (row.status === "rejected" || row.status === "cancelled") && (
                <span
                  className="max-w-[260px] truncate text-[12px] text-[var(--fg-muted)]"
                  title={row.decision_reason}
                >
                  {t(`leave.list.decision_reason_${row.status}`, {
                    reason: row.decision_reason,
                  })}
                </span>
              )}
          </div>
        );
      },
    },
    {
      id: "reason",
      header: t("leave.column.reason"),
      cell: (row): React.ReactNode => (
        <span className="font-serif text-[13.5px] italic text-[var(--fg-muted)]">
          {row.reason ?? t("leave.cell.no_reason")}
        </span>
      ),
    },
    {
      id: "actions",
      header: t("leave.column.actions"),
      align: "end",
      // Decision actions belong only to a `submitted` management row. Self
      // history may be viewed under an admin hat, but it must never advertise
      // a decision on the caller's own request; cancel stays on self history
      // while a request is pending. ScopeGate remains a role convenience — the
      // server re-checks every call.
      cell: (row): React.ReactNode => (
        <div className="flex justify-end gap-2">
          {row.leave_record_status === "issued" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(): void => {
                void downloadRecord(row);
              }}
            >
              <FileText size={13} aria-hidden="true" />
              {t("leave.action.record")}
            </Button>
          )}
          {isManagementView && row.status === "submitted" && (
            <>
              <ScopeGate required={LEAVE_CAP_APPROVE} allowed={capabilities}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={(): void => {
                    decisions.ask("approve", row);
                  }}
                >
                  {t("leave.action.approve")}
                </Button>
              </ScopeGate>
              <ScopeGate required={LEAVE_CAP_REJECT} allowed={capabilities}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(): void => {
                    decisions.ask("reject", row);
                  }}
                >
                  {t("leave.action.reject")}
                </Button>
              </ScopeGate>
            </>
          )}
          {!isManagementView && (row.status === "draft" || row.status === "submitted") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(): void => {
                decisions.ask("cancel", row);
              }}
            >
              {t("leave.action.cancel")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Self mounts one table in the active TabPanel; management calls the same
  // renderer directly because its status cannot change. In both paths,
  // `query.items` is already server-filtered (client re-filtering would hide
  // rows beyond the keyset window); `status` only picks the empty-state copy.
  const renderRows = (status: string): React.ReactNode => {
    const rows = query.items;
    if (rows.length === 0) {
      return isManagementView ? (
        <Empty
          title={t("leave.list.empty_management_title")}
          description={t("leave.list.empty_management_description")}
        />
      ) : status === "all" ? (
        <Empty title={t("leave.empty_title")} description={t("leave.empty_description")} />
      ) : (
        <Empty
          title={t("leave.list.empty_filtered_title")}
          description={t("leave.list.empty_filtered_description")}
        />
      );
    }
    return (
      <Card className="overflow-hidden">
        <DataTable<LeaveRequestRow>
          caption={t("leave.table_caption")}
          columns={columns}
          rows={rows}
          rowKey={(row): string => row.request_id}
          virtualize
          hasNextPage={query.hasNextPage}
          onLoadMore={query.fetchNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          loadMoreLabel={t("leave.load_more")}
        />
      </Card>
    );
  };

  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <PageHeader
        title={canDecide ? t("leave.list.title") : t("leave.list.title_self")}
        description={
          canDecide
            ? t("leave.list.lead", {
                total: formatCountWithMore(totalCount, summary.hasNextPage),
                pending: pendingCount,
              })
            : t("leave.list.lead_self", {
                total: formatCountWithMore(totalCount, summary.hasNextPage),
                pending: pendingCount,
              })
        }
        actions={
          // A decision inbox has no self-service primary: "Yeni talep" here
          // filed a leave for the manager reading their team's queue.
          isManagementView ? undefined : (
            <ScopeGate required={LEAVE_CAP_FILE} allowed={capabilities}>
              <Button variant="primary" asChild>
                <RouterLink to={newRequestHref}>
                  <Plus size={14} aria-hidden="true" />
                  {t("leave.list.new_request")}
                </RouterLink>
              </Button>
            </ScopeGate>
          )
        }
      />

      {/* Self history uses its unfiltered summary for a status breakdown; the
          submitted-only management inbox exposes only metrics that window can
          answer without implying visibility into terminal requests. */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("leave.list.kpi_pending")}
          value={formatCountWithMore(pendingCount, summary.hasNextPage)}
          tone="warning"
          icon={<Clock3 size={18} />}
        />
        {isManagementView ? (
          <>
            <StatCard
              label={t("leave.list.kpi_hours")}
              value={t(
                summary.hasNextPage
                  ? "leave.list.hours_value_plus"
                  : "leave.list.hours_value",
                {
                  hours: formatHours(totalHours, i18n.language) ?? t("common.unknown"),
                },
              )}
              tone="info"
              icon={<Hourglass size={18} />}
            />
            <StatCard
              label={t("leave.list.kpi_requesters")}
              value={formatCountWithMore(requesterCount, summary.hasNextPage)}
              tone="neutral"
              icon={<Users size={18} />}
            />
            <StatCard
              label={t("leave.list.kpi_earliest")}
              value={earliestStartLabel}
              tone="success"
              icon={<CalendarClock size={18} />}
            />
          </>
        ) : (
          <>
            <StatCard
              label={t("leave.list.kpi_approved")}
              value={formatCountWithMore(approvedCount, summary.hasNextPage)}
              tone="success"
              icon={<CircleCheck size={18} />}
            />
            <StatCard
              label={t("leave.list.kpi_rejected")}
              value={formatCountWithMore(rejectedCount, summary.hasNextPage)}
              tone="neutral"
              icon={<CircleX size={18} />}
            />
            <StatCard
              label={t("leave.list.kpi_total")}
              value={formatCountWithMore(totalCount, summary.hasNextPage)}
              tone="info"
              icon={<Inbox size={18} />}
            />
          </>
        )}
      </div>

      {recordFailed && (
        <Alert severity="danger" title={t("leave.record.download_failed_title")}>
          {t("leave.record.download_failed")}
        </Alert>
      )}

      {query.isLoading ? (
        <Loading label={t("leave.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("leave.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : isManagementView ? (
        renderRows(activeStatusTab)
      ) : (
        <Tabs value={activeStatusTab} onValueChange={setStatusTab} variant="segmented">
          {/* Per-tab count pills dropped: the table query is now status-scoped
              server-side, so a client per-tab tally over the loaded window would
              be wrong (it would only ever see the active tab's rows). The KPI
              strip above carries the self-history breakdown instead. */}
          <TabList aria-label={t("leave.list.tabs_label")}>
            {STATUS_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value}>
                {t(tab.labelKey)}
              </Tab>
            ))}
          </TabList>
          {STATUS_TABS.map((tab) => (
            <TabPanel key={tab.value} value={tab.value}>
              {renderRows(tab.value)}
            </TabPanel>
          ))}
        </Tabs>
      )}

      {decisions.layer}
    </div>
  );
}
